'use client';

import { useEffect, useRef } from 'react';

/**
 * Keeps a scrollable container pinned to the bottom whenever any of `deps`
 * change (typically: the message list and the stream status).
 *
 * Lives in a hook because that's the guide's accepted escape hatch for a
 * raw `useEffect` side-effect — the component using this gets a clean API
 * (`<div ref={ref}>…`) instead of an ambient effect leaking into render.
 */
export function useAutoScrollToBottom<T extends HTMLElement>(deps: ReadonlyArray<unknown>): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    // The deps array is the caller's responsibility — they know what
    // should trigger a scroll-to-bottom. We forward it verbatim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}